import type { FormObject } from "@formwerk/core";
import type { ComputedRef } from "vue";
import type { StandardSchemaV1 } from "@standard-schema/spec";
import { useForm } from "@formwerk/core";

export type FormHandler<TResult, TInput> = {
  schema: StandardSchemaV1<FormObject, FormObject>;
  action: string;
  initialValues?: ComputedRef<TInput | undefined> | TInput;
  onSuccess?: (result: TResult) => void;
  onError?: () => void;
};

export type FormResult<T> = | { status: "pending" } | { status: "success"; data: T } | { status: "error"; message: string };

export function useFormHandler<TResult, TInput = unknown>({ schema, action, initialValues, onSuccess, onError }: FormHandler<TResult, TInput>) {
  const result = ref<FormResult<TResult>>({ status: "pending" });
  const { handleSubmit, isSubmitting, reset, values, getErrors } = useForm({ schema, initialValues: unref(initialValues) || {} });
  const onSubmit = handleSubmit(async (params) => {
    try {
      const data = await ajaxRequest<TResult>(action, <AjaxParams>params.toObject());
      result.value = { status: "success", data };
      if (onSuccess) onSuccess(data);
    }
    catch (error) {
      result.value = { status: "error", message: error instanceof Error ? error.message : "Une erreur inconnue est survenue." };
      if (onError) onError();
    }
  });
  const onReset = () => {
    result.value = { status: "pending" };
    reset({ revalidate: false, value: unref(initialValues) || {} });
  };
  return { result, onSubmit, onReset, isSubmitting, values: values as TInput, getErrors };
}
